import React, { useEffect, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Card, Table, Button, Form, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import { DOTTUYENSINH_API } from '@app/utils/apiUrl';
import { DeleteSingle, Get } from '@app/utils/httpProvider';
import { createUrlWithParam, formatDateTimeWithoutHour } from '../../../../utils/helper';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import { messageToast, objectShowNumber, status200, styleSelectDefault, objectTrangThaiDotTuyenSinh, trangThaiDotTuyenSinhValue } from '../../../../utils/define';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const DotTuyenSinh = () => {
    const history = useHistory();
    const location = useLocation();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [nam, setNam] = useState('');
    const [trangThai, setTrangThai] = useState(trangThaiDotTuyenSinhValue.all);

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const params = new URLSearchParams(location.search.substring(1));
    if (params.get('page') != null && parseInt(params.get('page'), 10) !== page) {
        setPage(parseInt(params.get('page'), 10));
    }
    if (params.get('size') != null && parseInt(params.get('size'), 10) !== pageSize) {
        setPageSize(parseInt(params.get('size'), 10));
    }
    if (params.get('search') != null && params.get('search') !== search) {
        setSearch(params.get('search'));
    }

    const replaceHistory = (Page, Size, Search) => {
        history.replace(
            createUrlWithParam(URI_INTERNAL.DOTTUYENSINH_PATH, {
                page: Page,
                size: Size,
                search: Search
            })
        );
    };

    const { isLoading, isError, error, data, refetch } = useQuery(
        ['dotTuyenSinhData', valueSearch, page, pageSize, nam, trangThai],
        () => {
            const model = {
                Keyword: valueSearch || '',
                PageIndex: page,
                PageSize: pageSize,
            };

            if (nam) {
                model.Nam = Number(nam);
            }

            if (trangThai !== trangThaiDotTuyenSinhValue.all) {
                model.TrangThaiDotTuyenSinh = trangThai;
            }

            replaceHistory(page, pageSize, valueSearch);
            return Get(DOTTUYENSINH_API.GET_PAGING, null, model);
        },
        { keepPreviousData: true }
    );

    useEffect(() => {
        refetch();
    }, [pageSize, page, nam, trangThai]);

    useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            setPage(1);
            replaceHistory(1, pageSize, valueSearch);
        }
    }, [valueSearch]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, search);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(Number(e.target.value));
        replaceHistory(page, Number(e.target.value), search);
    };

    const deleteFunc = useMutation((value) => DeleteSingle(DOTTUYENSINH_API.DELETE_ITEM, value, null));

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(messageToast.messageToastDeleteError);
                }
            },
            onError: (err) => {
                toast.error(err?.message || messageToast.messageToastTryCatch);
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const items = data?.data?.$values ? data.data.$values : data?.data || [];
    const totalRecords = data?.totalRecords || 0;

    const getStatusLabel = (status) => {
        const found = objectTrangThaiDotTuyenSinh.find((x) => x.value === status);
        return found ? found.label : 'Không xác định';
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5>Danh sách đợt tuyển sinh</h5>
                        <div className="d-flex">
                            <NavLink to={URI_INTERNAL.DOTTUYENSINH_CREATE_PATH} className="btn btn-success">
                                Thêm mới
                            </NavLink>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <Form.Control
                                placeholder="Năm"
                                value={nam}
                                onChange={(e) => setNam(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Control
                                as="select"
                                value={trangThai}
                                onChange={(e) => setTrangThai(e.target.value)}
                            >
                                {objectTrangThaiDotTuyenSinh.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Control>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>STT</th>
                                    <th>Mã đợt</th>
                                    <th>Tên đợt</th>
                                    <th>Năm</th>
                                    <th>Từ ngày</th>
                                    <th>Đến ngày</th>
                                    <th>Trạng thái</th>
                                    <th>Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={8}>Đang tải...</td>
                                    </tr>
                                ) : isError ? (
                                    <tr>
                                        <td colSpan={8}>Lỗi: {error?.message || 'Không tải được dữ liệu'}</td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr>
                                        <td colSpan={8}>Không có dữ liệu</td>
                                    </tr>
                                ) : (
                                    items.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>{item.maDot || item.MaDot}</td>
                                            <td>{item.tenDot || item.TenDot}</td>
                                            <td>{item.nam || item.Nam}</td>
                                            <td>{formatDateTimeWithoutHour(item.tuNgay) || formatDateTimeWithoutHour(item.TuNgay) || '-'}</td>
                                            <td>{formatDateTimeWithoutHour(item.denNgay) || formatDateTimeWithoutHour(item.DenNgay) || '-'}</td>
                                            <td>
                                                <Badge variant="info">{getStatusLabel(item.trangThaiDotTuyenSinh || item.TrangThaiDotTuyenSinh)}</Badge>
                                            </td>
                                            <td>
                                                <NavLink
                                                    className="btn btn-primary btn-sm mr-2"
                                                    to={`${URI_INTERNAL.DOTTUYENSINH_EDIT_PATH}?id=${item.id}`}>
                                                    Sửa
                                                </NavLink>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => deleteAction(item.id, item.tenDot || item.TenDot)}>
                                                    Xóa
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <Form.Control as="select" value={pageSize} onChange={onChangeShowNumber} style={{ width: '100px' }}>
                                {objectShowNumber.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                ))}
                            </Form.Control>
                        </div>
                        <div>
                            <Pagination
                                itemClass="page-item"
                                linkClass="page-link"
                                innerClass="pagination pagination-sm no-margin"
                                activePage={page}
                                itemsCountPerPage={pageSize}
                                totalItemsCount={totalRecords}
                                pageRangeDisplayed={5}
                                onChange={changePage}
                            />
                        </div>
                    </div>

                    <ConfirmModal
                        isOpen={openConfirmDeleteModal}
                        confirmType="delete"
                        handleAccept={acceptDelete}
                        handleCancel={cancelDelete}
                        content={confirmDelMessage}
                    />
                </Card.Body>
            </Card>
        </div>
    );
};

export default DotTuyenSinh;
